import { useMutation } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useApi } from "~hooks/apiHooks/useApi";
import { useNotification } from "~hooks/useNotification";

type UploadTarget = { fileName: string; uploadUrl: string };

export const useImport = () => {
  const request = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  // The pre-signed upload bypasses useApi, so its failures are not reported by the useApi error handlers
  const failUpload = (error: unknown): never => {
    notification.error({ message: formatMessage({ id: "import.upload.failed" }), placement: "bottomRight" });
    throw error;
  };

  const uploadToPreSignedUrl = async (file: File, uploadTarget?: UploadTarget) => {
    if (!uploadTarget) {
      return failUpload(new Error(formatMessage({ id: "import.upload.url.missing" })));
    }

    const uploadResponse = await fetch(uploadTarget.uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    }).catch(failUpload);

    if (!uploadResponse.ok) {
      return failUpload(new Error(formatMessage({ id: "import.upload.failed.status" }, { status: uploadResponse.status })));
    }

    return uploadTarget.fileName;
  };

  return useMutation<unknown, Error, { file: File; startImporting: (fileName: string) => Promise<unknown> }>({
    mutationFn: async ({ file, startImporting }) => {
      const response = await request<{ data: Array<UploadTarget> }>(`import/upload-url`);
      const fileName = await uploadToPreSignedUrl(file, response?.data?.[0]);

      await startImporting(fileName);
    },
  });
};
